
import {Component, Mixins} from 'vue-property-decorator';
import CatalogEntryMixin from 'src/modules/catalog/components/cards/CatalogEntryMixin';
import {songService} from 'src/modules/playable/SongService';
import {MiniPlayerConfig} from "/src/modules/global/mixins/MiniPlayerConfig";
import {defaultActives, defaultVolumes, SoundParameters} from 'src/modules/player/models/parameters/SoundParameters';
import {getScoreDescription} from 'src/modules/masteries/MasteriesUtils';
import DifficultyIndicator from 'src/modules/catalog/components/cards/indicators/DifficultyIndicator.vue';
import TeacherNotice from 'src/modules/teacher/TeacherNotice.vue';
import CatalogMiniatureWithInformation from 'src/modules/catalog/components/cards/parts/CatalogMiniatureWithInformation.vue';
import {challengeService} from 'src/modules/playable/ChallengeService';
import MiniPlayerMixin from 'src/modules/global/mixins/MiniPlayerMixin';
import {SwipeToDismissMixin} from 'src/modules/global/mixins/SwipeToDismissMixin';
import {userService} from 'src/modules/user/UserService';
import {homeController} from '../../services/HomeController';
import {playClassService} from 'src/modules/play-class/PlayClassService';
import {preloadImage} from 'src/utils/helpers';

@Component({
  components: {
  CatalogMiniatureWithInformation,
  TeacherNotice,
  DifficultyIndicator,
  },
  })
export default class CatalogEntryPopup extends Mixins(MiniPlayerMixin, SwipeToDismissMixin) {
  private isDisplayed: boolean = false;
  private catalogEntryMixin: CatalogEntryMixin | null = null;
  private allowPreview = true;

  public show(catalogEntry: CatalogEntryMixin, allowPreview: boolean = true) {
    this.catalogEntryMixin = catalogEntry;
    this.allowPreview = allowPreview;
    this.$nextTick(() => (this.isDisplayed = true));
    const piece = catalogEntry.catalogEntry.piece;
    preloadImage(piece.image_large && piece.image_large !== 'none' ? piece.image_large : piece.image);

    if (allowPreview) {
      const defaultSoundParameters = new SoundParameters(defaultActives, defaultVolumes);
      if (this.catalogEntryMixin.isChallenge) {
        challengeService.loadChallenge(this.catalogEntryMixin.catalogEntry.id, false).then(() => {
          this.load(
            new MiniPlayerConfig(
              challengeService.state.challengeDivisions,
              challengeService.state.currentChallenge,
              defaultSoundParameters
            )
          );
        });
      }
      else {
        songService.loadVersion(this.catalogEntryMixin.catalogEntry.id).then(() => {
          this.load(
            new MiniPlayerConfig(songService.state.divisions, songService.state.currentVersion, defaultSoundParameters)
          );
        });
      }
    }
  }

  protected dismissSheet() {
    this.isDisplayed = false;
  }

  private onCloseDialog() {
    this.onSheetHide();
    this.isDisplayed = false;
    this.catalogEntryMixin = null;
    this.close();
  }

  get handName() {
    return this.catalogEntryMixin.catalogEntry.config.hand_type === 'BOTH_HAND'
      ? this.$t('catalogEntryPopup.bothHands')
      : this.catalogEntryMixin.catalogEntry.config.hand_type === 'RIGHT_HAND'
        ? this.$t('catalogEntryPopup.rightHand')
        : this.$t('catalogEntryPopup.leftHand');
  }

  get scoreDescription() {
    return getScoreDescription(
      this.catalogEntryMixin.songScore,
      this.catalogEntryMixin.currentDifficulty,
      this.isVersionNotInteresting,
      this.catalogEntryMixin.isChallenge ? 'challenge' : 'morceau',
      this.catalogEntryMixin.progress
    );
  }

  get showAsActionPopup() {
    return this.$q.screen.gt.sm || this.$q.screen.width < this.$q.screen.height;
  }

  get isAlreadyInPieceCourses() {
    return userService.user.configurations.pieceCourseIds.includes(this.catalogEntryMixin.catalogEntry.id);
  }

  get isVersionRecommended() {
    return !this.catalogEntryMixin.isChallenge && homeController.state.pieceRecommendations.some((el) => el.id === this.catalogEntryMixin?.catalogEntry.id);
  }

  get isVersionNotInteresting() {
    return !this.catalogEntryMixin.isChallenge && userService.user.configurations.notInterestingVersions.includes(this.catalogEntryMixin.catalogEntry.id);
  }

  get hasStartedClass() {
    return !this.catalogEntryMixin.isChallenge && (this.catalogEntryMixin.progress ?? 0) > 0;
  }

  get isClassFinished() {
    return !this.catalogEntryMixin.isChallenge && (this.catalogEntryMixin.progress ?? 0) >= 100;
  }

  playFree() {
    const entry = this.catalogEntryMixin;
    const id = entry.catalogEntry.id;
    const artist = entry.catalogEntry.piece.url_artist ?? '_';
    const title = entry.catalogEntry.piece.url_title ?? '_';
    this.$router.push(`/player/version/${id}/${artist}/${title}`);
  }

  resumeCourse() {
    const entry = this.catalogEntryMixin;
    const id = entry.catalogEntry.id;
    const artist = entry.catalogEntry.piece.url_artist ?? '_';
    const title = entry.catalogEntry.piece.url_title ?? '_';

    const navigate = (classId?: number) => {
      const path = `/learn/version/${id}/${artist}/${title}`;
      this.$router.push(classId ? `${path}?classId=${classId}` : path);
    };

    const resolveClassId = (): number | undefined => {
      const lastActive = playClassService.lastActiveClass;
      if (lastActive && !lastActive.progress.isFinished) return lastActive.id;
      const partClasses = playClassService.state.rootClass?.content?.PIANO_BOTH?.containedClasses ?? [];
      for (const part of partClasses) {
        if (part.containedClasses.length > 0) {
          const section = part.containedClasses.find((s) => !s.progress.isFinished);
          if (section) return section.id;
        } else if (!part.progress.isFinished) {
          return part.id;
        }
      }
      return undefined;
    };

    const rootClass = playClassService.state.rootClass;
    if (rootClass?.playableContent?.id === id) {
      navigate(resolveClassId());
    } else {
      playClassService.loadClass(id).then(() => navigate(resolveClassId()));
    }
  }

  markAsInterest() {
    const notInterestingVersions = userService.user.configurations.notInterestingVersions
    notInterestingVersions.push(this.catalogEntryMixin.catalogEntry.id)
    userService.updateConfiguration('NOT_INTERESTING_VERSIONS', notInterestingVersions).then(() => {
      homeController.loadPieceCourses(true);
      this.onCloseDialog()
    })
  }

  addToPieceCourses() {
    const pieceCourseIds = userService.user.configurations.pieceCourseIds;
    pieceCourseIds.push(this.catalogEntryMixin.catalogEntry.id);
    userService.updateConfiguration('PIECE_COURSES', pieceCourseIds);
  }
}
